import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { map } from 'rxjs/operators';
import { Observable, switchMap, of } from 'rxjs';

import { NgxHttpClient } from '@app/shared/ngx-http-client';
import { ApiServiceBase } from '@app/shared/services/api-service-base';
import { catchAndSurfaceError } from '@app/shared/utils/dg-error-helpers';
import {
  Attribute,
  AttributeDataType,
  AttributesApiService,
} from '@dg/attributes-data-access';

import {
  LoadOptions,
  LoadRulesResponse,
  BusinessRule,
  RuleTypes,
  StaticAttributes,
} from '../model';

const enum SortDirection {
  Descending = 'desc',
  Ascending = 'asc',
}

@Injectable({
  providedIn: 'root',
})
export class BusinessRulesApiService extends ApiServiceBase {
  private i18n = this.translate.instant(['Core_Error']);

  constructor(
    http: NgxHttpClient,
    private translate: TranslateService,
    private attributesApiService: AttributesApiService
  ) {
    super(http, translate.instant('OrgSvc_OrgError'));
  }

  public validateName(ruleName, orgId, ruleType): Observable<boolean> {
    return this.http.get<boolean>(
      `/organizations/${orgId}/businessrules/checkRuleNameExists`,
      {
        params: { ruleName, ruleType },
      }
    );
  }
  public loadRules(
    orgId: number,
    options: LoadOptions
  ): Observable<LoadRulesResponse> {
    return this.http
      .get<LoadRulesResponse>(`/organizations/${orgId}/businessrules`, {
        params: {
          pageNumber: options.currentPage,
          pageSize: options.pageSize,
          orderBy: `${options.orderBy} ${
            options.isDescending
              ? SortDirection.Descending
              : SortDirection.Ascending
          }`,
          searchTerm: options.searchTerm,
          businessRuleType: options.businessRuleType,
          getAll: options.getAll,
        },
      })
      .pipe(
        map((response: LoadRulesResponse) => {
          response.data.forEach((item: BusinessRule) => {
            item.id = item.businessRuleId.toString();
          });
          return response;
        }),
        catchAndSurfaceError(this.i18n.Core_Error) // TODO: more descriptive error text
      );
  }

  public deleteRule(orgId: number, businessRuleId: number) {
    return this.http
      .delete(`/organizations/${orgId}/businessrules/${businessRuleId}`)
      .pipe(
        catchAndSurfaceError(this.i18n.Core_Error) // TODO: more descriptive error text
      );
  }

  public upsertRule(
    businessRule: Partial<BusinessRule>
  ): Observable<BusinessRule> {
    let request$: Observable<BusinessRule>;
    if (!businessRule.businessRuleId) {
      delete businessRule.businessRuleId; // if the object property exists, but is undefined, the request will fail
      // new rule
      request$ = this.http.post<BusinessRule>(
        `/organizations/${businessRule.organizationId}/businessrules`,
        businessRule
      );
    } else {
      // existing rule
      request$ = this.http.put<BusinessRule>(
        `/organizations/${businessRule.organizationId}/businessrules/${businessRule.businessRuleId}`,
        businessRule
      );
    }

    return request$.pipe(
      map((rule) => {
        rule.id = rule.businessRuleId.toString();
        return rule;
      }),
      catchAndSurfaceError(this.i18n.Core_Error) // TODO: more descriptive error text
    );
  }

  public setIsActive(orgId: number, isActive: boolean, businessRuleId: number) {
    return this.http
      .put(`/organizations/${orgId}/businessrules/${businessRuleId}/isActive`, {
        isActive,
      })
      .pipe(catchAndSurfaceError(this.i18n.Core_Error));
  }

  public loadAttributeNames(
    orgId: number,
    ruleType: RuleTypes
  ): Observable<Attribute[]> {
    return this.attributesApiService.getAttributeNames(orgId, ruleType).pipe(
      switchMap((attributes) => {
        const applyAll: Attribute = {
          attributeName: StaticAttributes.APPLY_TO_ALL_USERS,
          attributeDataType: AttributeDataType.String,
        };
        const applyNew: Attribute = {
          attributeName: StaticAttributes.APPLY_TO_NEW_USERS,
          attributeDataType: AttributeDataType.String,
        };

        return of(
          ruleType === RuleTypes.BusinessRule
            ? [applyAll, applyNew].concat(attributes)
            : attributes
        );
      })
    );
  }

  public loadActionOptions(
    orgId: number,
    businessRuleType?: RuleTypes
  ): Observable<string[]> {
    return this.http
      .get(`/organizations/${orgId}/businessrules/actions`, {
        params: { businessRuleType },
      })
      .pipe(catchAndSurfaceError(this.i18n.Core_Error));
  }
}
